import { computed, shallowRef } from 'vue';
import { pointLngLat } from './normalize';
import type { Campus, DirectusCampus, LngLat } from './types';

// Built-in list, used until (or unless) the Directus `campuses` collection loads.
// Codes match the `campus` choices on Directus `locations`. Clinton Crossings and Highland
// Hospital have no locations yet but are still useful places to jump the camera to.
const builtIn: Campus[] = [
  { code: 'rc', name: 'River Campus', center: [-77.6286, 43.1287], zoom: 16 },
  { code: 'mc', name: 'Medical Center', center: [-77.6258, 43.122], zoom: 16.2 },
  { code: 'midc', name: 'Middle Campus', center: [-77.6304, 43.119], zoom: 16.4 },
  { code: 'sc', name: 'South Campus', center: [-77.6337, 43.1127], zoom: 16.2 },
  { code: 'mh', name: 'Mt. Hope Campus', center: [-77.6153, 43.1348], zoom: 16 },
  { code: 'ec', name: 'Eastman Campus', center: [-77.6012, 43.1582], zoom: 17 },
  { code: 'mag', name: 'Memorial Art Gallery', center: [-77.5879, 43.1575], zoom: 17 },
  { code: 'highland', name: 'Highland Hospital', center: [-77.6065, 43.136], zoom: 16.4 },
  { code: 'clinton-crossings', name: 'Clinton Crossings', center: [-77.5966, 43.1133], zoom: 16.4 },
];

/** Reactive list: templates and computeds update when Directus data replaces the built-ins. */
export const campuses = shallowRef<Campus[]>(builtIn);

/** Where the map starts when the URL doesn't name a campus. */
export const defaultCampus = builtIn[0]!;

const byCode = computed(() => new Map(campuses.value.map((c) => [c.code, c])));

/** Replace the built-in list with Directus rows; rows without a usable center are skipped. */
export function setCampuses(rows: DirectusCampus[]): void {
  const list: Campus[] = [];
  for (const row of rows) {
    const center = pointLngLat(row.center);
    if (!row.code || !row.name || !center) continue;
    list.push({ code: row.code, name: row.name, center, zoom: row.zoom ?? 16 });
  }
  if (list.length) campuses.value = list;
}

export function campusByCode(code: string | null | undefined): Campus | undefined {
  return code ? byCode.value.get(code) : undefined;
}

export function campusName(code: string): string {
  return byCode.value.get(code)?.name ?? code.toUpperCase();
}

/** Rough ground distance in meters; fine at campus scale. */
function distanceMeters([lng1, lat1]: LngLat, [lng2, lat2]: LngLat): number {
  const x = (lng2 - lng1) * Math.cos(((lat1 + lat2) / 2) * (Math.PI / 180));
  const y = lat2 - lat1;
  return Math.sqrt(x * x + y * y) * 111_320;
}

/** The campus the camera is looking at, if it's close to one and zoomed in enough. */
export function campusAt(center: LngLat, zoom: number, maxMeters = 700): Campus | undefined {
  if (zoom < 14) return undefined;
  let best: Campus | undefined;
  let bestDistance = maxMeters;
  for (const campus of campuses.value) {
    const d = distanceMeters(center, campus.center);
    if (d < bestDistance) {
      best = campus;
      bestDistance = d;
    }
  }
  return best;
}
